package com.arenaproplay.admin.state.shell

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.compositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.arenaproplay.admin.domain.admin.AdminRepository
import kotlinx.coroutines.delay

/**
 * Live counts shown in the sidebar nav pills.
 *
 * - [onlineCount]  — authenticated users connected via WebSocket right now.
 *                    Polled from [AdminRepository.getOnlineNow] every 60 s.
 * - [supportCount] — open/unread support messages.  The admin API does not
 *                    expose a cheap unread-count endpoint; we derive it from
 *                    the first page of support messages (limit 1 → just the
 *                    total) so we don't over-fetch.  Set to 0 on any error.
 *
 * Both fields start at null (loading / not yet fetched) so the pill can be
 * hidden while data is in flight.
 */
data class ShellCounts(
    val onlineCount: Int? = null,
    val supportCount: Int? = null,
)

val LocalShellCounts = compositionLocalOf { ShellCounts() }

/**
 * Remembers and continuously refreshes [ShellCounts].
 *
 * Call this only inside an authenticated layout — it guards against a null
 * [repo] and simply returns empty counts when auth is not ready (e.g. the
 * Login page renders briefly before the redirect fires).
 */
@Composable
fun rememberShellCounts(repo: AdminRepository?): ShellCounts {
    var counts by remember { mutableStateOf(ShellCounts()) }

    LaunchedEffect(repo) {
        if (repo == null) return@LaunchedEffect
        while (true) {
            val online = try {
                repo.getOnlineNow().online
            } catch (_: Throwable) {
                counts.onlineCount // keep last known value
            }

            val support = try {
                val page = repo.getSupportMessages(limit = 1, offset = 0)
                page.total.toInt().coerceAtLeast(0)
            } catch (_: Throwable) {
                counts.supportCount // keep last known value
            }

            counts = ShellCounts(onlineCount = online, supportCount = support)
            delay(60_000)
        }
    }

    return counts
}
